/**
 * i18n Configuration for EzERP Platform
 * Supports: English (en), Portuguese Brazil (pt-BR), Spanish (es)
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import enCommon from './locales/en/common.json';
import enAuth from './locales/en/auth.json';
import enNavigation from './locales/en/navigation.json';
import enSettings from './locales/en/settings.json';
import enWms from './locales/en/wms.json';
import enAdmin from './locales/en/admin.json';
import enErrors from './locales/en/errors.json';
import enNotifications from './locales/en/notifications.json';
import enMarketingHub from './locales/en/marketing_hub.json';

import ptBRCommon from './locales/pt-BR/common.json';
import ptBRAuth from './locales/pt-BR/auth.json';
import ptBRNavigation from './locales/pt-BR/navigation.json';
import ptBRSettings from './locales/pt-BR/settings.json';
import ptBRWms from './locales/pt-BR/wms.json';
import ptBRAdmin from './locales/pt-BR/admin.json';
import ptBRErrors from './locales/pt-BR/errors.json';
import ptBRNotifications from './locales/pt-BR/notifications.json';
import ptBRMarketingHub from './locales/pt-BR/marketing_hub.json';

import esCommon from './locales/es/common.json';
import esAuth from './locales/es/auth.json';
import esNavigation from './locales/es/navigation.json';
import esSettings from './locales/es/settings.json';
import esWms from './locales/es/wms.json';
import esAdmin from './locales/es/admin.json';
import esErrors from './locales/es/errors.json';
import esNotifications from './locales/es/notifications.json';
import esMarketingHub from './locales/es/marketing_hub.json';

// Supported languages configuration
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)', flag: '🇧🇷' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
];

// Get language name by code
export const getLanguageName = (code) => {
  const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
  return lang ? lang.nativeName : code;
};

// Resources object with all translations
const resources = {
  en: {
    common: enCommon,
    auth: enAuth,
    navigation: enNavigation,
    settings: enSettings,
    wms: enWms,
    admin: enAdmin,
    errors: enErrors,
    notifications: enNotifications,
    marketing_hub: enMarketingHub,
  },
  'pt-BR': {
    common: ptBRCommon,
    auth: ptBRAuth,
    navigation: ptBRNavigation,
    settings: ptBRSettings,
    wms: ptBRWms,
    admin: ptBRAdmin,
    errors: ptBRErrors,
    notifications: ptBRNotifications,
    marketing_hub: ptBRMarketingHub,
  },
  es: {
    common: esCommon,
    auth: esAuth,
    navigation: esNavigation,
    settings: esSettings,
    wms: esWms,
    admin: esAdmin,
    errors: esErrors,
    notifications: esNotifications,
    marketing_hub: esMarketingHub,
  },
};

// Language detection options
const detectionOptions = {
  // Order of language detection
  order: ['localStorage', 'cookie', 'navigator', 'htmlTag'],
  // Keys to use for storage
  lookupLocalStorage: 'ezerp_language',
  lookupCookie: 'ezerp_language',
  // Cache user language
  caches: ['localStorage', 'cookie'],
  // Cookie options
  cookieMinutes: 525600, // 1 year
  cookieOptions: { path: '/', sameSite: 'strict' },
};

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    defaultNS: 'common',
    ns: ['common', 'auth', 'navigation', 'settings', 'wms', 'admin', 'errors', 'notifications', 'marketing_hub'],

    detection: detectionOptions,

    interpolation: {
      escapeValue: false, // React already escapes values
      format: (value, format, lng) => {
        // Custom formatting for dates, numbers, currency
        if (format === 'date') {
          return new Intl.DateTimeFormat(lng).format(value);
        }
        if (format === 'datetime') {
          return new Intl.DateTimeFormat(lng, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(value);
        }
        if (format === 'number') {
          return new Intl.NumberFormat(lng).format(value);
        }
        if (format === 'currency') {
          const currencyCode = lng === 'pt-BR' ? 'BRL' : lng === 'es' ? 'EUR' : 'USD';
          return new Intl.NumberFormat(lng, {
            style: 'currency',
            currency: currencyCode,
          }).format(value);
        }
        if (format === 'percent') {
          return new Intl.NumberFormat(lng, {
            style: 'percent',
            minimumFractionDigits: 1,
          }).format(value / 100);
        }
        return value;
      },
    },

    // React specific options
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'em', 'b', 'span'],
    },

    // Debug mode (disable in production)
    debug: process.env.NODE_ENV === 'development',

    // Save missing keys (for development)
    saveMissing: process.env.NODE_ENV === 'development',
    missingKeyHandler: (lngs, ns, key, fallbackValue) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[i18n] Missing translation: ${ns}:${key} for languages: ${lngs.join(', ')}`);
      }
    },
  });

// Function to change language and persist in the browser
export const changeLanguage = async (languageCode) => {
  // Change i18n language
  await i18n.changeLanguage(languageCode);

  // Persist to localStorage
  localStorage.setItem('ezerp_language', languageCode);

  // Update document language attribute
  document.documentElement.lang = languageCode;

  return languageCode;
};

// Function to get current language
export const getCurrentLanguage = () => {
  return i18n.language || 'en';
};

// Function to format date based on current locale
export const formatDate = (date, options = {}) => {
  const lng = getCurrentLanguage();
  return new Intl.DateTimeFormat(lng, options).format(new Date(date));
};

// Function to format number based on current locale
export const formatNumber = (number, options = {}) => {
  const lng = getCurrentLanguage();
  return new Intl.NumberFormat(lng, options).format(number);
};

// Function to format currency based on current locale
export const formatCurrency = (amount, currencyCode = null) => {
  const lng = getCurrentLanguage();
  const defaultCurrency = lng === 'pt-BR' ? 'BRL' : lng === 'es' ? 'EUR' : 'USD';
  return new Intl.NumberFormat(lng, {
    style: 'currency',
    currency: currencyCode || defaultCurrency,
  }).format(amount);
};

export default i18n;
